import { LifestyleRulesPageTemplateProps } from "./template";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { LIFESTYLE_YEARLY_AMOUNT_LIMIT } from "@config/lifestyle";

export const useLifestyleRulesPage = (): LifestyleRulesPageTemplateProps => {
    const navigate = useNavigate();
    const location = useLocation();
    const [yearlyAmount, setYearlyAmount] = useState<number>(1000);
    const [coveragePercentage, setCoveragePercentage] = useState<number>(80);
    const [allocationType, setAllocationType] = useState<'pro-rata' | 'full-amount'>('pro-rata');
    
    // Get data from navigation state
    const selectedCategories = location.state?.selectedCategories || [];
    const lifestyleGroupName = location.state?.lifestyleGroupName || "";
    
    const clampYearlyAmount = (value: number) =>
        Math.min(LIFESTYLE_YEARLY_AMOUNT_LIMIT.max, Math.max(LIFESTYLE_YEARLY_AMOUNT_LIMIT.min, value));

    // Restore rules data from navigation state when returning to this page
    useEffect(() => {
        if (location.state?.rules) {
            const { yearlyAmount: savedAmount, coveragePercentage: savedCoverage, allocationType: savedAllocation } = location.state.rules;
            if (savedAmount !== undefined) setYearlyAmount(clampYearlyAmount(savedAmount));
            if (savedCoverage !== undefined) setCoveragePercentage(savedCoverage);
            if (savedAllocation !== undefined) setAllocationType(savedAllocation);
        }
    }, [location.state]);

    const navigateToPage = (to: string) => {
        navigate(to);
    }

    const onReturnPressed = () => {
        // Pass data back to categories page
        navigate("/dashboard/lifestyle/create-group", {
            state: { 
                selectedCategories,
                lifestyleGroupName,
                rules: {
                    yearlyAmount,
                    coveragePercentage,
                    allocationType
                }
            }
        });
    }

    const onYearlyAmountChange = (value: number) => {
        setYearlyAmount(clampYearlyAmount(value));
    }

    const onCoveragePercentageChange = (value: number) => {
        setCoveragePercentage(value);
    }

    const onAllocationTypeChange = (type: 'pro-rata' | 'full-amount') => {
        setAllocationType(type);
    }

    const onContinue = () => {
        // Navigate to the next step (confirm page) with all data
        navigate("/dashboard/lifestyle/confirm", {
            state: {
                selectedCategories,
                lifestyleGroupName,
                rules: {
                    yearlyAmount,
                    coveragePercentage,
                    allocationType
                }
            }
        });
    }

    return {
        navigateToPage,
        onReturnPressed,
        yearlyAmount,
        onYearlyAmountChange,
        coveragePercentage,
        onCoveragePercentageChange,
        allocationType,
        onAllocationTypeChange,
        onContinue
    }
} 
